// "Will someone come by and ticket?" from 52 weeks of LADOT citations.
//
// For each ~150 m cell and hour of the week, the build counts how many weeks had at least one
// ticket written within one cell (~200 m) during that clock hour. That fraction is the chance an
// officer is working nearby in that hour. For a stay that spans several hours we assume
// independence: P(nobody) = Π (1 − p_h)^(overlap of hour h). A backtest on held-out weeks
// (data/out/stats.json → risk_backtest) shows this is well calibrated, slightly high above ~70%.

import { local, holidayType, HOUR, type HolidayType } from './time.ts'

export interface RiskCell {
  w: Uint8Array // 168 counts (weeks with a ticket within ~2 blocks), Mon 0:00 first
  b: Uint8Array // the same for this ~150 m cell alone (about one block)
  ratio: number // Σb / Σw: how much of the neighbourhood's ticketing happens on this block
  dom: string // 168 chars, index into meta.cats, '-' = none
  cats: number[] // tickets by category within ~2 blocks over the year
}

// Block counts are sparse, so each hour borrows strength from the neighbourhood: κ pseudo-weeks
// at (this block's share × the neighbourhood rate). κ = 8 had the best held-out calibration.
const KAPPA = 8

export interface RiskMeta {
  weeks: number
  validDays: number[] // usable (non-holiday) days per weekday, Mon first
  cats: string[]
  start: string
  end: string
}

// la-parking/data/policy_priors/enforcement_policy.json
const HOLIDAY_POLICY: Record<HolidayType, [number, number]> = {
  federal: [0.15, 0.03], black_friday: [0.45, 0.08], pre_thanksgiving: [0.6, 0.1], christmas_eve: [0.3, 0.06], nye: [0.3, 0.06],
}

export const CAT_LABEL: Record<string, string> = {
  meter: 'expired meters', sweep: 'street cleaning', zone: 'red or loading curbs', nostop: 'no-stopping zones',
  permit: 'permit streets', limit: 'time limits', reg: 'expired tags', other: 'other violations',
}

export type Level = 'low' | 'some' | 'likely' | 'very'

export interface RiskHour {
  ms: number // start of the clock hour
  p: number // chance of a ticket being written nearby during that hour
  frac: number // how much of the hour your stay covers
  dom: string | null // what usually gets ticketed then
}

export interface Risk {
  covered: boolean // false outside the City of LA's data
  p: number // chance a ticket is written within ~2 blocks at some point in your stay
  pBlock: number // … on this block (~150 m)
  level: Level // of pBlock: what matters for your car
  hours: RiskHour[] // every clock hour your stay touches
  peak: RiskHour | null
  day: number[] // the first day's 24 hourly chances, for the sparkline
  dayStart: number // ms of that day's local midnight
  mix: { cat: string; label: string; share: number }[]
  holiday: HolidayType | null
  scope?: 'street' // set when the source only records streets (West Hollywood): pBlock is an estimate
  street?: string
  enforcer?: string // when not covered: whose curb it is (ParkMe agency code: SM, BH, LAC, UCLA…)
}

export function levelOf(p: number): Level {
  return p < 0.1 ? 'low' : p < 0.3 ? 'some' : p < 0.6 ? 'likely' : 'very'
}

function holiday(p: number, date: string): number {
  const h = holidayType(date)
  if (!h) return p
  const [mult, cap] = HOLIDAY_POLICY[h]
  return Math.min(p * mult, cap)
}

export function hourP(cell: RiskCell, meta: RiskMeta, dow: number, hour: number, date: string): number {
  const valid = meta.validDays[dow] || meta.weeks
  return holiday(Math.min(1, cell.w[dow * 24 + hour] / valid), date)
}

export function blockP(cell: RiskCell, meta: RiskMeta, dow: number, hour: number, date: string): number {
  const valid = meta.validDays[dow] || meta.weeks
  const i = dow * 24 + hour
  const prior = cell.ratio * Math.min(1, cell.w[i] / valid)
  return holiday(Math.min(1, (cell.b[i] + KAPPA * prior) / (valid + KAPPA)), date)
}

export function risk(cell: RiskCell | null, meta: RiskMeta, start: number, end: number, covered = true): Risk {
  const hours: RiskHour[] = []
  let none = 1
  const first = local(start)
  const dayStart = start - first.min * 60_000
  const day: number[] = []
  if (cell) for (let h = 0; h < 24; h++) day.push(hourP(cell, meta, first.dow, h, first.date))
  // Walk clock hours. Stepping by real hours keeps DST days right (23 or 25 local hours).
  let noneBlock = 1
  let t = start - (first.min % 60) * 60_000
  while (t < end) {
    const l = local(t)
    const a = Math.max(t, start), b = Math.min(t + HOUR, end)
    const frac = Math.max(0, (b - a) / HOUR)
    const hourOfDay = Math.floor(l.min / 60)
    const p = cell ? hourP(cell, meta, l.dow, hourOfDay, l.date) : 0
    const pb = cell ? blockP(cell, meta, l.dow, hourOfDay, l.date) : 0
    const d = cell ? cell.dom[l.dow * 24 + hourOfDay] : '-'
    hours.push({ ms: t, p, frac, dom: d && d !== '-' ? meta.cats[Number(d)] : null })
    none *= Math.pow(1 - Math.min(p, 0.999), frac)
    noneBlock *= Math.pow(1 - Math.min(pb, 0.999), frac)
    t += HOUR
  }
  const p = 1 - none
  const pBlock = 1 - noneBlock
  const peak = hours.reduce<RiskHour | null>((m, h) => (h.frac > 0 && (!m || h.p > m.p) ? h : m), null)
  const total = cell ? cell.cats.reduce((a, b) => a + b, 0) : 0
  const mix = cell && total
    ? cell.cats.map((n, i) => ({ cat: meta.cats[i], label: CAT_LABEL[meta.cats[i]] ?? meta.cats[i], share: n / total }))
      .filter((m) => m.share >= 0.08).sort((a, b) => b.share - a.share).slice(0, 3)
    : []
  return { covered, p, pBlock, level: levelOf(pBlock), hours, peak, day, dayStart, mix, holiday: holidayType(first.date) }
}

const bytes = (b64: string) => Uint8Array.from(atob(b64), (c) => c.charCodeAt(0))

export function decodeCell(row: { w: string; b?: string; dom: string; cats: string }): RiskCell {
  const w = bytes(row.w)
  const b = row.b ? bytes(row.b) : new Uint8Array(168)
  const sw = w.reduce((x, y) => x + y, 0), sb = b.reduce((x, y) => x + y, 0)
  return { w, b, ratio: sw ? Math.min(1, sb / sw) : 0, dom: row.dom, cats: JSON.parse(row.cats) }
}

export interface StreetRow { w: Uint8Array; q: Uint8Array; len: number; cats: number[] }

/**
 * Odds from street-level records (West Hollywood). p: a ticket somewhere on the street in WeHo.
 * pBlock: officer passes per hour (distinct quarter-hours with tickets) thinned to one block's share
 * of the street, as a Poisson rate: an estimate, and labelled as one.
 */
export function streetRisk(row: StreetRow, valid: number[], cats: string[], street: string, start: number, end: number): Risk {
  const share = Math.min(1, 150 / Math.max(150, row.len))
  const first = local(start)
  const dayStart = start - first.min * 60_000
  const hp = (dow: number, h: number, date: string) => holidayDamp(Math.min(1, row.w[dow * 24 + h] / (valid[dow] || 52)), date)
  const bp = (dow: number, h: number, date: string) => holidayDamp(1 - Math.exp(-(row.q[dow * 24 + h] / (valid[dow] || 52)) * share), date)
  const day = Array.from({ length: 24 }, (_, h) => hp(first.dow, h, first.date))
  const hours: RiskHour[] = []
  let none = 1, noneBlock = 1
  let t = start - (first.min % 60) * 60_000
  while (t < end) {
    const l = local(t)
    const frac = Math.max(0, (Math.min(t + HOUR, end) - Math.max(t, start)) / HOUR)
    const h = Math.floor(l.min / 60)
    const p = hp(l.dow, h, l.date)
    hours.push({ ms: t, p, frac, dom: null })
    none *= Math.pow(1 - Math.min(p, 0.999), frac)
    noneBlock *= Math.pow(1 - Math.min(bp(l.dow, h, l.date), 0.999), frac)
    t += HOUR
  }
  const pBlock = 1 - noneBlock
  const peak = hours.reduce<RiskHour | null>((m, h) => (h.frac > 0 && (!m || h.p > m.p) ? h : m), null)
  const total = row.cats.reduce((a, b) => a + b, 0)
  const mix = total ? row.cats.map((n, i) => ({ cat: cats[i], label: CAT_LABEL[cats[i]] ?? cats[i], share: n / total }))
    .filter((m) => m.share >= 0.08).sort((a, b) => b.share - a.share).slice(0, 3) : []
  return { covered: true, p: 1 - none, pBlock, level: levelOf(pBlock), hours, peak, day, dayStart, mix, holiday: holidayType(first.date), scope: 'street', street }
}

function holidayDamp(p: number, date: string): number {
  return holiday(p, date)
}

export function decodeStreet(row: { w: string; q: string; len: number; cats: string }): StreetRow {
  return { w: bytes(row.w), q: bytes(row.q), len: row.len, cats: JSON.parse(row.cats) }
}
